/*
 * Authors: mariushirjanu, Koen
 * Created: 30/10/2015
 * Upgraded: 20/03/2023
 */
import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { IExtraFieldConfigurationObject } from '../../../../../shared/formly/ga-formly.service';
import { OrderEntryInvoiceService } from './order-entry-invoice.service';
import { OrderEntryLocationFormService } from '../order-entry-location-form.service';
import { IAddressData } from '../base-order-entry-location-to.service';
import { IInvoiceAddress, InvoiceAddressType, OrderEntryAddressType } from '../order-entry-location-data.service';
import { BILLINGTYPE_CENTRAL } from '../../../../../shared/services/billing/billing-data.service';
import { GaValidateService, IValidatingComponent } from '../../../../../shared/formly/ga-validate.service';
import { GaDeferredService, IGADeferred } from '../../../../../shared/services/ga-deferred.service';
import { ILocationExtended } from '../../../../../../type_definitions/datatypes';

interface IEInvoiceModel {
  eInvoiceMailAddress:string;
  editable:boolean;
}

@Component({
  selector: 'order-location-einvoice',
  template: `
    <div class="e-invoice" *ngIf="fields?.length">
      <h2 translate>{{'ORDERENTRY.INVOICE.E_INVOICE.TITLE'}}</h2>
      <div class="row">
        <div class="col-md-9 e-invoice-form">
          <formly [form]="form" [fields]="fields" [model]="model" class="form-horizontal"></formly>
        </div>
      </div>
    </div>
  `
})
export class OrderLocationEinvoiceComponent implements IValidatingComponent, OnInit, OnChanges, AfterViewInit {
  @Input() public locationToModel:OrderEntryInvoiceService;
  @Input() public billingType:string;
  @Input() public fixedList:boolean;
  @Input() public addressType:InvoiceAddressType;
  @Input() public location:ILocationExtended;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IEInvoiceModel = <any>{};
  public loading:boolean;
  private onValidationReadyDeferred:IGADeferred<void>;
  private selectedAddress:IAddressData;
  private loadedAddress:IInvoiceAddress;

  constructor(
    private orderEntryLocationFormService:OrderEntryLocationFormService,
    private validateService:GaValidateService,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.initModel();
    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.addressType) {
      this.addressType = changes.addressType.currentValue;
      this.selectedAddress = this.locationToModel.getAddress(this.addressType);
      this.loadedAddress = <IInvoiceAddress>this.locationToModel.getLoadedAddress(this.addressType);

      // 1 from address
      // 2 from location
      this.setEInvoiceMailAddress(this.getEInvoiceMailAddressForType(this.addressType));
    }

    if (changes.location) {
      this.location = changes.location.currentValue;
      this.setEInvoiceMailAddress(this.getEInvoiceMailAddressForType(this.addressType) || this.location?.invoiceEmailAddress);
    }
  }

  private initModel():void {
    // 1. locationModel
    let eInvoiceMailAddress:string = this.locationToModel.eInvoiceMailAddress;

    // 2. default from address
    if (!eInvoiceMailAddress && this.addressType) {
      eInvoiceMailAddress = this.getEInvoiceMailAddressForType(<InvoiceAddressType>this.addressType);
    }

    // 3. default from location
    if (!eInvoiceMailAddress && this.addressType) {
      eInvoiceMailAddress = this.location?.invoiceEmailAddress;
    }

    // 4. default from shipping location
    if (!eInvoiceMailAddress && this.addressType) {
      eInvoiceMailAddress = this.locationToModel.selectedAddress?.location?.invoiceEmailAddress;
    }

    this.setEInvoiceMailAddress(eInvoiceMailAddress, true);
  }

  private getEInvoiceMailAddressForType(addressType:InvoiceAddressType):string {
    if (!addressType) {
      return;
    }

    let invoiceEmailAddress:string = this.loadedAddress?.invoiceEmailAddress || this.selectedAddress?.location?.invoiceEmailAddress;
    // it has the same address as the shipping location -> have to get the invoice email address from addressModels

    // looking also in addressModels except for existing locations where invoiceEmailAddress is always selected from the Location field
    if (addressType !== OrderEntryAddressType.EXISTING_LOCATION && !invoiceEmailAddress) {
      invoiceEmailAddress = this.locationToModel.addressData?.invoiceEmailAddress;
    }
    return invoiceEmailAddress;
  }

  private listen():void {
    // store einvoice form changes to locationToModel to restore when coming back to the page
    this.form.valueChanges.subscribe((model:IEInvoiceModel) => {
      this.locationToModel.eInvoiceMailAddress = model.eInvoiceMailAddress;
      this.locationToModel.eInvoiceMailAddressEditable = model.editable;
    });
  }

  private setEInvoiceMailAddress(eInvoiceMailAddress:string, forceEditable?:boolean, forceOverwrite?:boolean):void {
    // if forceOverwrite is false (means this not coming when init model), field is editable (no default invoice address)
    // currently e-invoice email address field is filled but eInvoiceMailAddress parameter is undefined then don't overwrite the model
    if (!forceOverwrite && this.model.editable && this.model.eInvoiceMailAddress && !eInvoiceMailAddress) {
      return;
    }

    this.model.eInvoiceMailAddress = eInvoiceMailAddress;
    if (!forceEditable && eInvoiceMailAddress && (this.fixedList || this.billingType === BILLINGTYPE_CENTRAL)) { // not editable
      this.fields = [
        this.orderEntryLocationFormService.createEInvoiceMailAddressValueField()
      ];
      this.model.editable = false;
    } else {
      this.fields = [
        this.orderEntryLocationFormService.createEInvoiceMailAddressField()
      ];
      this.model.editable = true;
    }
  }

  public beforeValidate = ():Promise<any> => {
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<void|string[]> => {
    return this.validateService.validateFields(this.fields, this.model, true);
  };
}